/**
 * coverHover — single floating preview attached to .row__cover hover.
 *
 * One element on body. Delegated mouseenter/leave handlers; 250ms debounce
 * (so wiping the mouse across the table doesn't spawn dozens of fetches);
 * per-item JS-side cache so re-hover is instant.
 *
 * Server endpoint: GET /api-zapd/v1/items/<id>.
 * Privacy: server returns 404 for admin-only paths to non-admins.
 */
import { api } from '../core/api.js';
import { icon } from '../core/icons.js';
import { COVER_HOVER_HIDE_MS } from '../core/timing.js';

const HOVER_DELAY_MS = 250;
const cardCache = new Map(); // item id → cover-data | 'NEG' | Promise
let card = null;
let timer = null;
let lastHash = null;

function ensureCard() {
  if (card) return card;
  card = document.createElement('div');
  card.className = 'cover-hover';
  card.setAttribute('role', 'tooltip');
  document.body.appendChild(card);
  return card;
}

function escHtml(s) {
  return String(s ?? '').replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c],
  );
}

function renderCard(c) {
  if (!c) return '<div class="cover-hover__empty">Kein Match</div>';
  const meta = [];
  if (c.kind === 'tv' && c.season != null) {
    const epPart = c.episode != null ? ` E${String(c.episode).padStart(2, '0')}` : '';
    meta.push(`📺 S${String(c.season).padStart(2, '0')}${epPart}`);
  }
  if (c.year) meta.push(escHtml(c.year));
  if (c.runtime_min) meta.push(`${c.runtime_min} min`);
  if (c.vote_average) meta.push(`★ ${parseFloat(c.vote_average).toFixed(1)}`);
  const archiveNote =
    c.archive_state === 'rar_only'
      ? `<div class="cover-hover__archive">📦 Nur Archiv — Play nach Extraktion</div>`
      : c.archive_state === 'iso'
        ? `<div class="cover-hover__archive">💿 Disc-Image (Blu-ray/DVD)</div>`
        : '';
  return `
    ${c.poster_url ? `<img src="${escHtml(c.poster_url)}" alt="">` : ''}
    <div class="cover-hover__title">${escHtml(c.title ?? '?')}</div>
    ${meta.length ? `<div class="cover-hover__meta">${meta.join(' · ')}</div>` : ''}
    ${c.tagline ? `<div class="cover-hover__tagline">${escHtml(c.tagline)}</div>` : ''}
    ${c.overview ? `<div class="cover-hover__plot">${escHtml(c.overview).slice(0, 300)}${c.overview.length > 300 ? '…' : ''}</div>` : ''}
    ${c.director ? `<div class="cover-hover__crew">Regie: ${escHtml(c.director)}</div>` : ''}
    ${c.genres_csv ? `<div class="cover-hover__genres">${escHtml(c.genres_csv)}</div>` : ''}
    ${c.trailer_url ? `<a class="cover-hover__trailer" href="${escHtml(c.trailer_url)}" target="_blank" rel="noopener">${icon('film', { size: 14 })} Trailer öffnen</a>` : ''}
    ${archiveNote}
  `;
}

function position(el) {
  const rect = el.getBoundingClientRect();
  const w = card.offsetWidth || 280;
  const h = card.offsetHeight || 180;
  // Prefer right of the row__cover; fall back to left if it would clip.
  let left = rect.right + 8;
  if (left + w > window.innerWidth - 12) left = rect.left - w - 8;
  if (left < 12) left = 12;
  let top = rect.top + rect.height / 2 - h / 2;
  if (top < 12) top = 12;
  if (top + h > window.innerHeight - 12) top = window.innerHeight - h - 12;
  card.style.left = left + 'px';
  card.style.top = top + 'px';
}

async function show(hashEl) {
  const itemId = hashEl.dataset.itemId;
  if (!itemId) return;
  if (lastHash === itemId && card?.classList.contains('is-on')) return;
  lastHash = itemId;
  ensureCard();

  const archive = hashEl.dataset.archive || '';
  let cached = cardCache.get(itemId);

  if (cached === undefined) {
    card.innerHTML = '<div class="cover-hover__loading">…</div>';
    card.classList.add('is-on');
    position(hashEl);
    const promise = api(`/api-zapd/v1/items/${encodeURIComponent(itemId)}`)
      .then((d) => {
        const cover = d
          ? { ...d, runtime_min: Math.round(Number(d.duration_ms || 0) / 60000) }
          : null;
        cardCache.set(itemId, cover ?? 'NEG');
        return cover;
      })
      .catch(() => {
        cardCache.set(itemId, 'NEG');
        return null;
      });
    cardCache.set(itemId, promise); // store promise so concurrent hovers wait
    cached = await promise;
  } else if (cached === 'NEG') {
    card.innerHTML = renderCard(null);
    card.classList.add('is-on');
    position(hashEl);
    return;
  } else if (cached instanceof Promise) {
    cached = await cached;
  }

  if (lastHash !== itemId) return; // user moved on while we were fetching
  if (!cached || cached === 'NEG') {
    card.innerHTML = renderCard(null);
  } else {
    // Server might omit archive_state if covers row missing — fall back to data attr
    cached.archive_state = cached.archive_state || archive;
    card.innerHTML = renderCard(cached);
  }
  card.classList.add('is-on');
  position(hashEl);
}

function hide() {
  if (!card) return;
  card.classList.remove('is-on');
  lastHash = null;
}

export function mountCoverHover(rootEl = document) {
  rootEl.addEventListener('mouseover', (e) => {
    const cov = e.target.closest?.('.row__cover');
    if (!cov) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => show(cov), HOVER_DELAY_MS);
  });
  rootEl.addEventListener('mouseout', (e) => {
    const cov = e.target.closest?.('.row__cover');
    if (!cov) return;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    setTimeout(hide, COVER_HOVER_HIDE_MS);
  });
  // Hide on scroll/resize so card doesn't drift
  window.addEventListener('scroll', hide, { passive: true });
  window.addEventListener('resize', hide);
}
